import { TimeAgo } from '@awpmarine/ui/components/time-ago';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@awpmarine/ui/components/ui/breadcrumb';
import { ParaglideMessage } from '@inlang/paraglide-js-react';
import { Link } from '@tanstack/react-router';
import type { TableOfContents } from 'fumadocs-core/toc';
import type { MDXProps } from 'mdx/types';
import type * as React from 'react';

import { ContentLanguageNotice, contentLang } from '@/components/content-language';
import { MarkdownDocs } from '@/components/markdown';
import { languageTag } from '@/lib/locale';
import { m } from '@/paraglide/messages';

import { DocsToc } from './-page.toc';

interface LegalDocumentProps {
  title: string;
  published: string;
  lastModified?: Date | string;
  toc: TableOfContents;
  body: React.FC<MDXProps>;
  /** Written in the page's locale, rather than standing in from English. */
  translated: boolean;
}

/** A legal document or company policy: breadcrumb, title and dates, then the text beside its contents. */
export function LegalDocument({ title, published, lastModified, toc, body, translated }: LegalDocumentProps) {
  return (
    <>
      <section className='mx-auto max-w-310 px-5 pt-12 pb-10 sm:px-8 sm:pt-14'>
        <Breadcrumb className='mb-5'>
          <BreadcrumbList className='gap-2 text-base sm:gap-2'>
            <BreadcrumbItem>
              <BreadcrumbLink render={<Link to='/legal' />} className='hover:text-brand'>
                {m.legal_breadcrumb()}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage className='text-subtle'>{title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <h1
          lang={contentLang(translated)}
          className='mb-3 text-[clamp(38px,4.4vw,56px)] leading-[1.08] font-bold tracking-[-0.02em]'
        >
          {title}
        </h1>
        <p className='text-subtle'>
          {lastModified ? (
            <ParaglideMessage
              message={m.legal_published_updated}
              inputs={{}}
              markup={{
                published: () => <TimeAgo value={published} locale={languageTag()} />,
                updated: () => <TimeAgo value={lastModified} locale={languageTag()} />,
              }}
            />
          ) : (
            <ParaglideMessage
              message={m.legal_published}
              inputs={{}}
              markup={{ published: () => <TimeAgo value={published} locale={languageTag()} /> }}
            />
          )}
        </p>
        <ContentLanguageNotice translated={translated} binding />
      </section>
      <section className='mx-auto flex max-w-310 items-start gap-14 px-5 pb-16 sm:px-8 sm:pb-24'>
        <DocsToc toc={toc} />
        <article lang={contentLang(translated)} className='max-w-190 min-w-0 flex-1'>
          <MarkdownDocs body={body} />
        </article>
      </section>
    </>
  );
}
